import { Link, useParams } from 'react-router-dom';
import { useState, useMemo } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { animeList, categories, siteInfo } from '@/mocks/anime';

export default function Category() {
  const { slug = 'all' } = useParams();
  const [activeTab, setActiveTab] = useState(slug);

  const filteredAnime = useMemo(() => {
    if (activeTab === 'all') return animeList;
    return animeList.filter((a) => a.category === activeTab);
  }, [activeTab]);

  const activeCategory = categories.find((c) => c.slug === activeTab);

  return (
    <>
      <ScrollToTop />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify([
            {
              '@context': 'https://schema.org',
              '@type': 'BreadcrumbList',
              itemListElement: [
                {
                  '@type': 'ListItem',
                  position: 1,
                  name: '首页',
                  item: window.location.origin,
                },
                {
                  '@type': 'ListItem',
                  position: 2,
                  name: activeCategory?.name || '全部',
                },
              ],
            },
            {
              '@context': 'https://schema.org',
              '@type': 'CollectionPage',
              name: `樱花动漫 - ${activeCategory?.name || '全部'}动漫`,
              description: `在樱花动漫网浏览${activeCategory?.name || '全部'}动漫作品，发现好看的${activeCategory?.name || ''}动漫推荐`,
              url: window.location.href,
              isPartOf: {
                '@type': 'WebSite',
                name: siteInfo.name,
                url: window.location.origin,
              },
            },
          ]),
        }}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="max-w-7xl mx-auto px-4 md:px-6 py-8 md:py-10">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: activeCategory?.name || '全部动漫' },
            ]}
          />

          <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mt-2 mb-1">
            {activeCategory?.name || '全部'}动漫
          </h1>
          <p className="text-sm text-foreground-500 mb-8">
            共找到 {filteredAnime.length} 部作品
          </p>

          {/* Category Tabs */}
          <div className="flex items-center gap-1.5 mb-8 overflow-x-auto pb-2">
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                onClick={() => setActiveTab(cat.slug)}
                className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${
                  activeTab === cat.slug
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-100 text-foreground-600 hover:bg-background-200/70'
                }`}
              >
                <i className={`${cat.icon} text-sm`}></i>
                {cat.name}
              </Link>
            ))}
          </div>

          {/* Anime Grid */}
          {filteredAnime.length > 0 ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5" data-product-shop="">
              {filteredAnime.map((anime) => (
                <Link
                  key={anime.id}
                  to={`/article/${anime.id}`}
                  className="group cursor-pointer"
                >
                  <div className="relative aspect-[2/3] rounded-xl overflow-hidden mb-3">
                    <img
                      src={anime.image}
                      alt={anime.title}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute top-3 left-3">
                      <span className="px-2.5 py-1 rounded-full bg-primary-500 text-background-50 text-xs font-medium">
                        {anime.rating}
                      </span>
                    </div>
                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      <p className="text-background-100 text-xs line-clamp-3">
                        {anime.description}
                      </p>
                    </div>
                  </div>
                  <h3 className="font-heading text-sm md:text-base font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-1">
                    {anime.title}
                  </h3>
                  <div className="flex items-center gap-2 mt-1.5">
                    <span className="text-xs text-foreground-500">
                      {anime.status}
                    </span>
                    <span className="text-xs text-foreground-400">·</span>
                    <span className="text-xs text-foreground-500">
                      {anime.episodes}集
                    </span>
                  </div>
                  <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
                    {anime.tags.map((tag) => (
                      <span
                        key={tag}
                        className="px-2 py-0.5 rounded-md bg-secondary-100 text-secondary-800 text-xs"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100 text-foreground-400">
                <i className="ri-emotion-sad-line text-2xl"></i>
              </div>
              <p className="text-foreground-500">该分类暂无作品</p>
            </div>
          )}
        </main>

        <Footer />
      </div>
    </>
  );
}